import { union } from 'ramda';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { User, UserId } from 'api/models';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { Empty, Loaded, Loading } from 'core/state';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { TextWrapper } from 'ozone/atoms';
import { fullName } from 'utils/full-name';
import { ContainedList } from '../notifications/list/line-item/contained-list';

interface Props {
  userId: UserId;
  approverIds: Empty | Loading | Loaded<number[]>;
}

const Title = styled(TextWrapper)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
  padding: 0;
`;

const Wrapper = styled.div`
  display: flex;
  gap: 12px;
`;

export const TimesheetsHeader: FC<Props> = ({
  userId: [, userId],
  approverIds,
}) => {
  const activeUsers = useGetAllActiveUsersMap();
  const { user: currentUser } = useUser();
  const selfId = currentUser.userId;
  const youText = useTranslation('you.capitalized');
  const approvers = useMemo(
    () =>
      (approverIds[1] ?? [])
        .map((x) => activeUsers[x])
        .sort((user) => (user?.userId === selfId ? -1 : 0)) as User[],
    [activeUsers, approverIds, selfId],
  );
  const user = activeUsers[userId];
  const users = union(union(approvers, [user]), [currentUser])
    .sort((u) => (u?.userId === selfId ? -1 : 0))
    .map((u) => (u?.userId === selfId ? youText : u?.firstName));

  return (
    <Wrapper>
      <AvatarGroup maxCount={3}>
        {approvers.map((x) => (
          <Avatar
            size="small"
            key={x?.userId}
            name={fullName(x)}
            src={x?.photoUrl}
          />
        ))}
      </AvatarGroup>
      <Title>
        <ContainedList items={users} show={1000} />
      </Title>
    </Wrapper>
  );
};
